import type { CurrencyCode, TransportKind } from '../domain/types';
import type { LlmProvider, ResolvedParser } from '../state/settings';
import { anthropicExtractor } from './anthropic';
import { geminiExtractor } from './gemini';
import { openrouterExtractor } from './openrouter';

/** One transport leg extracted from the user's screenshot and/or note. All
 * fields are optional — the user reviews them in the segment dialog. */
export interface ExtractedLeg {
  depCity?: string;
  depAddr?: string;
  /** `datetime-local` string (`YYYY-MM-DDTHH:MM`), local to the departure place. */
  depTime?: string;
  /** Locally parsed clock when the screenshot does not contain a usable date. */
  depClock?: string;
  arrCity?: string;
  arrAddr?: string;
  /** `datetime-local` string (`YYYY-MM-DDTHH:MM`), local to the arrival place. */
  arrTime?: string;
  /** Locally parsed clock when the screenshot does not contain a usable date. */
  arrClock?: string;
  transport?: TransportKind;
  company?: string;
  /** Number of transfers/connections (0 = direct). */
  transfers?: number;
  /** Free-form transfer details (intermediate cities, durations). */
  transfersInfo?: string;
  cost?: number;
  currency?: CurrencyCode;
}

/** One hotel stay extracted from the user's screenshot and/or note. */
export interface ExtractedHotel {
  name?: string;
  city?: string;
  addr?: string;
  /** `datetime-local` strings (`YYYY-MM-DDTHH:MM`). */
  checkIn?: string;
  checkOut?: string;
  /** Local OCR may recover dates while leaving the unknown times blank. */
  checkInDate?: string;
  checkOutDate?: string;
  cost?: number;
  currency?: CurrencyCode;
}

/** What the user provides for recognition: screenshots (possibly several,
 * e.g. a listing plus a details view), a free-form note, or both. */
export interface ExtractInput {
  files: File[];
  note: string;
}

/** Result of an auto-detect extraction: transport legs or one hotel stay. */
export type AutoExtract = { legs: ExtractedLeg[] } | { hotel: ExtractedHotel };

/** A pluggable LLM backend that turns the input into records. */
export interface LegExtractor {
  extract(input: ExtractInput, parser: ResolvedParser): Promise<ExtractedLeg[]>;
  extractHotel(input: ExtractInput, parser: ResolvedParser): Promise<ExtractedHotel>;
  /** Decide leg-vs-hotel from the input itself, then extract accordingly. */
  extractAuto(input: ExtractInput, parser: ResolvedParser): Promise<AutoExtract>;
}

/** Thrown when the provider rejects the parser's API key. */
export class AuthError extends Error {}

const extractors: Record<LlmProvider, LegExtractor> = {
  gemini: geminiExtractor,
  openrouter: openrouterExtractor,
  anthropic: anthropicExtractor,
};

export const getExtractor = (parser: ResolvedParser): LegExtractor => extractors[parser.provider];
